import axios from 'axios';

// Perbaikan baseURL untuk menggunakan endpoint yang benar
// Hapus trailing slash di akhir baseURL untuk mencegah double slash
const baseURL = process.env.REACT_APP_API_URL || 
               (window.location.hostname.includes('rumahakrilik.id') ? 
               'https://rumahakrilik.id/api' : // Perubahan dari https://api.rumahakrilik.id
               'http://localhost:8000/api');    // Tambahkan /api di localhost juga

console.log(`Initializing API client with baseURL: ${baseURL}`);

// Buat instance axios dengan konfigurasi yang tepat
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://rumahakrilik.id/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Tambahkan fungsi createRequestConfig yang hilang
const createRequestConfig = (options = {}) => {
  const token = localStorage.getItem('jwtToken') || localStorage.getItem('token');
  
  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Authorization': token ? `Bearer ${token}` : '',
    }
  };
};

// Interceptor untuk menangani error 404 dan retry dengan URL alternatif
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Jika error 404 dan belum pernah dicoba dengan URL alternatif
    if (error.response?.status === 404 && !originalRequest._retry404) {
      originalRequest._retry404 = true;
      
      // Endpoint yang bermasalah
      const failedUrl = originalRequest.url;
      console.log(`Endpoint not found: ${failedUrl}. Trying alternative URL format.`);
      
      // Coba beberapa format URL alternatif
      let alternativeUrl = failedUrl;
      
      // 1. Cek jika URL tidak memiliki awalan slash, tambahkan slash
      if (!failedUrl.startsWith('/')) {
        alternativeUrl = '/' + failedUrl;
      }
      
      // 2. Cek jika URL menggunakan format /api/ di awal, coba hapus
      if (failedUrl.startsWith('/api/')) {
        alternativeUrl = failedUrl.substring(4); // Hapus /api
      } else if (failedUrl.startsWith('/')) {
        // 3. Jika tidak, coba tambahkan /api di awal
        alternativeUrl = '/api' + failedUrl;
      }
      
      console.log(`Trying alternative path format for: ${failedUrl}`);
      console.log(`Attempting to retry with: ${alternativeUrl}`);
      
      // Clone request asli dan ganti URL-nya
      originalRequest.url = alternativeUrl;
      return axios(originalRequest);
    }
    
    return Promise.reject(error);
  }
);

// Di file api.js, verifikasi interceptor request
api.interceptors.request.use(
  (config) => {
    // Coba ambil token dari localStorage
    const token = localStorage.getItem('jwtToken') || localStorage.getItem('token');
    
    // Jika token ada, tambahkan ke header
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Perbaikan untuk URL campaign yang konsisten
    if (config.url && (
        config.url.includes('/marketing-campaigns') || 
        config.url === '/marketing-campaigns/' ||
        config.url === 'marketing-campaigns/' ||
        config.url === 'marketing-campaigns'
      )) {
      // Pastikan URL konsisten dengan yang terbukti berhasil di curl test
      config.url = '/api/marketing-campaigns/';
    }
    
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Di formatApiPath, tambahkan timestamp untuk cache busting
const formatApiPath = (path) => {
  console.log(`Before formatting: ${path}`);
  
  // Pastikan path dimulai dengan /
  if (!path.startsWith('/')) {
    path = '/' + path;
  }
  
  // Tambahkan /api/ di depan jika tidak ada
  if (!path.startsWith('/api/')) {
    path = '/api' + path;
  }
  
  // Add cache busting for GET requests
  if (!path.includes('?')) {
    path = `${path}?_t=${Date.now()}`;
  } else {
    path = `${path}&_t=${Date.now()}`;
  }
  
  console.log(`Formatted API path with cache busting: ${path}`);
  return path;
};

// Definisikan metode get yang lebih robust
const get = async (path, options = {}) => {
  const formattedPath = formatApiPath(path);
  console.log(`Making GET request to: ${formattedPath}`);
  
  try {
    const response = await axios.get(formattedPath, createRequestConfig(options));
    return response;
  } catch (error) {
    console.error(`Error fetching ${formattedPath}:`, error);
    
    // Try alternative path format if 404
    if (error.response && error.response.status === 404) {
      console.log(`Endpoint not found: ${formattedPath}. Trying alternative URL format.`);
      
      // Try without /api prefix
      const altPath = path.startsWith('/') ? path : `/${path}`;
      console.log(`Trying alternative path format for: ${path}`);
      console.log(`Attempting to retry with: ${altPath}`);
      
      try {
        const altResponse = await axios.get(altPath, createRequestConfig(options));
        return altResponse;
      } catch (altError) {
        throw altError;
      }
    }
    
    throw error;
  }
};

// Menambahkan metode get ke api
api.get = get;

// PERBAIKAN: Method POST untuk menghindari duplikasi URL
api.post = async (path, data = {}, options = {}) => {
  // Prevent https://rumahakrilik.id/api/https://rumahakrilik.id/api/marketing-campaigns/
  if (path.includes('https://') || path.includes('http://')) {
    console.warn('Full URL detected in path argument. Extracting endpoint path only.');
    try {
      const url = new URL(path);
      path = url.pathname;
      console.log(`Extracted path: ${path}`);
    } catch (e) {
      console.error('Failed to parse URL:', e);
    }
  }
  
  const formattedPath = formatApiPath(path);
  console.log(`Making POST request to: ${formattedPath}`);
  
  try {
    const response = await axios.post(formattedPath, data, createRequestConfig(options));
    return response;
  } catch (error) {
    console.error(`Error posting to ${formattedPath}:`, error);
    
    // Jika 404, coba satu alternatif format path saja
    if (error.response && error.response.status === 404) {
      // Coba endpoint tanpa /api/ jika path saat ini dimulai dengan /api/
      // atau dengan /api/ jika tidak
      const altPath = path.startsWith('/api/') ? path.substring(4) : `/api${path.startsWith('/') ? path : '/' + path}`;
      
      console.log(`404 error, trying alternative format: ${altPath}`);
      
      try {
        const altResponse = await axios.post(altPath, data, createRequestConfig(options));
        return altResponse;
      } catch (altError) {
        throw altError;
      }
    }
    
    throw error;
  }
};

// Perbaiki fungsi api.put
api.put = async (path, data = {}, options = {}) => {
  // Definisikan formattedPath di luar blok try...catch agar tersedia di kedua blok
  const formattedPath = formatApiPath(path);
  
  try {
    console.log("Making PUT request with API wrapper:", path, data);
    console.log("Formatted URL:", formattedPath);
    
    const headers = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      ...((options.headers || {}))
    };
    
    // Add token to headers if available
    const token = localStorage.getItem('jwtToken') || localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    console.log("Request Headers:", headers);
    
    const response = await axios.put(formattedPath, data, { 
      ...options,
      headers 
    });
    
    console.log("API PUT response:", response);
    
    return response;
  } catch (error) {
    console.error(`Error putting to ${formattedPath}:`, error);
    
    // Try alternative path if 404
    if (error.response && error.response.status === 404) {
      const altPath = path.startsWith('/') ? path : `/${path}`;
      console.log(`Trying alternative path: ${altPath}`);
      try {
        const altResponse = await axios.put(altPath, data, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Authorization': localStorage.getItem('jwtToken') || localStorage.getItem('token') 
              ? `Bearer ${localStorage.getItem('jwtToken') || localStorage.getItem('token')}` 
              : '',
            ...((options.headers || {}))
          }
        });
        return altResponse;
      } catch (altError) {
        throw altError;
      }
    }
    
    throw error;
  }
};

// Update delete method untuk menghindari cache
api.delete = async (path, options = {}) => {
  const formattedPath = formatApiPath(path);
  console.log(`Making DELETE request to: ${formattedPath}`);
  
  try {
    // Gunakan fetch native untuk DELETE
    const token = localStorage.getItem('jwtToken') || localStorage.getItem('token');
    const response = await fetch(formattedPath, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok && response.status !== 204) {
      throw new Error(`Delete failed with status ${response.status}`);
    }
    
    return { status: response.status };
  } catch (error) {
    console.error(`Error deleting ${formattedPath}:`, error);
    
    if (error.response && error.response.status === 404) {
      const altPath = path.startsWith('/') ? path : `/${path}`;
      try {
        const token = localStorage.getItem('jwtToken') || localStorage.getItem('token');
        const altResponse = await fetch(altPath, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        
        if (!altResponse.ok && altResponse.status !== 204) {
          throw new Error(`Delete with alt path failed: ${altResponse.status}`);
        }
        
        return { status: altResponse.status };
      } catch (altError) {
        throw altError;
      }
    }
    
    throw error;
  }
};

// Menambahkan method untuk menghasilkan data mock
api.generateFallbackData = () => {
  const today = new Date();
  const plannedVisits = [];
  const completedVisits = [];
  
  // Generate 5 planned visits
  for (let i = 1; i <= 5; i++) {
    const visitDate = new Date();
    visitDate.setDate(today.getDate() + i);
    
    plannedVisits.push({
      id: 1000 + i,
      tanggal: visitDate.toISOString().split('T')[0],
      jam: '09:00',
      tujuan_kunjungan: `Toko Akrilik ${i}`,
      toko: `Toko Akrilik ${i}`,
      alamat: `Jl. Contoh No. ${i}, Jakarta`,
      kontak_person: `Kontak ${i}`,
      kecamatan: `Kecamatan ${i}`,
      kecamatan_nama: `Kecamatan ${i}`,
      telp: `08123456${i}`,
      tipe_kunjungan: 'Akuisisi',
      catatan: `Catatan untuk kunjungan ${i}`,
      status: 'rencana'
    });
  }
  
  // Generate 3 completed visits
  for (let i = 1; i <= 3; i++) {
    const visitDate = new Date();
    visitDate.setDate(today.getDate() - i);
    
    completedVisits.push({
      id: 2000 + i,
      tanggal: visitDate.toISOString().split('T')[0],
      jam: '10:00',
      toko: `Toko Completed ${i}`,
      tujuan_kunjungan: `Toko Completed ${i}`,
      alamat: `Jl. Selesai No. ${i}`,
      kontak_person: `Kontak ${i}`,
      telp: `08765432${i}`,
      hasil: i === 1 ? 'Order Placed' : 'Follow Up',
      catatan: `Kunjungan selesai ${i}`,
      tindak_lanjut: i === 1 ? 'Proses order' : 'Follow up minggu depan',
      status: 'selesai'
    });
  }
  
  return { plannedVisits, completedVisits };
};

// Tambahkan fungsi untuk save response data ke localStorage pada response interceptor
axios.interceptors.response.use(
  (response) => {
    console.log(`✓ ${response.config.method.toUpperCase()} ${response.config.url} success:`, response.status);
    
    // Save marketing plans response data untuk debugging
    if (response.config.url && response.config.url.includes('/marketing/plans')) {
      try {
        localStorage.setItem('lastAPIResponse', JSON.stringify({
          data: response.data,
          timestamp: new Date().toISOString()
        }));
        console.log("Saved marketing plans response for debugging");
      } catch (e) {
        console.warn("Could not save response to localStorage:", e);
      }
    }
    
    return response;
  },
  (error) => {
    // Error handler tetap sama
    return Promise.reject(error);
  }
);

// Export default api object
export default api;

